import uniq from 'lodash/uniq';
import without from 'lodash/without';
import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import {
  PENDING_RECEIPT,
  PARTIALLY_RECEIVED,
  CLOSED,
  CLOSED_ABANDONED,
} from '../../../../constants/constant_transaction_status';
import { PurchaseOrderForm } from '../../../../constants/constant_form_names';
import { SERIALIZED } from '../../../../constants/constant_item_types';

const selector = formValueSelector(PurchaseOrderForm);

function getEnableItemPartQuantityPreference(state) {
  return ((state.preference || {}).settings || {}).enable_item_part_quantity;
}

function getFormId(state) {
  return selector(state, 'id');
}

function getFormItemLines(state) {
  return selector(state, 'lines') || [];
}

function getFormPurchaseReceipts(state) {
  return selector(state, 'purchase_receipts') || [];
}

function getPropViewMode(state, viewMode) {
  return viewMode;
}

function getFormCurrency(state) {
  const currency = selector(state, 'currency') || {};
  return currency;
}

function getFormLocation(state) {
  const location = selector(state, 'location') || {};
  return location;
}

function getFormSubsidiary(state) {
  const subsidiary = selector(state, 'subsidiary') || {};
  return subsidiary;
}

function getFormReferenceId(state) {
  return selector(state, 'reference_id');
}

export const getReferenceId = createSelector(
  [getFormReferenceId],
  referenceId => {
    return referenceId;
  },
);

export const getCurrencyCode = createSelector(
  [getFormCurrency],
  currency => {
    return currency.code || 'USD';
  },
);

export const getCurrencySymbol = createSelector(
  [getFormCurrency],
  currency => {
    return currency.symbol || '$';
  },
);

export const getLocation = createSelector(
  [getFormLocation],
  location => {
    return location;
  },
);

export const getSubsidiary = createSelector(
  [getFormSubsidiary],
  subsidiary => {
    return subsidiary;
  },
);

export const getSubTotal = createSelector(
  [getFormItemLines],
  itemLines => {
    return itemLines.reduce((subTotal, itemLine) => {
      const { total_ex_tax } = itemLine;
      const result = Number(subTotal) + Number(total_ex_tax);
      return result;
    }, 0);
  },
);

export const getTotalTax = createSelector(
  [getFormItemLines],
  itemLines => {
    return itemLines.reduce((totalTax, itemLine) => {
      const { total_ex_tax, total } = itemLine;
      const tax = Number(total) - Number(total_ex_tax);
      const result = Number(totalTax) + Number(tax);
      return result;
    }, 0);
  },
);

export const getTotal = createSelector(
  [getFormItemLines],
  itemLines => {
    return itemLines.reduce((totalAmount, itemLine) => {
      const { total } = itemLine;
      const result = Number(total) + Number(totalAmount);
      return result;
    }, 0);
  },
);

export const hasOnEditModeLine = createSelector(
  [getFormItemLines],
  itemLines => {
    if (!itemLines.length) return false;
    return itemLines.some(itemLine => {
      return (
        itemLine &&
        itemLine.item &&
        !itemLine.quantity &&
        !itemLine.item_id &&
        itemLine.onEditMode
      );
    });
  },
);

export const getEditModeLineIndex = createSelector(
  [getFormItemLines],
  itemLines => {
    if (!itemLines.length) return null;
    return itemLines.findIndex(itemLine => {
      return itemLine && itemLine.onEditMode;
    });
  },
);

const getOwnLine = (state, index) => {
  return selector(state, `lines[${index}]`) || {};
};

const getOwnBrand = state => {
  return selector(state, 'brand_ids') || [];
};

const getOwnClassification = state => {
  return selector(state, 'classification_ids') || [];
};

const getOwnAlternateClass = state => {
  return selector(state, 'alternate_classification_ids') || [];
};

const getOwnLocationCurrency = state => {
  return selector(state, 'location_currency') || {};
};

const getOwnSupplierCurrency = state => {
  return selector(state, 'supplier_currency') || {};
};

const getFormStatus = state => {
  return selector(state, 'status') || {};
};

export const getExcludedItems = createSelector(
  [getFormItemLines, getOwnLine],
  (itemLines, ownLine) => {
    const selectedItemIds = itemLines.map(itemLine => itemLine.item_id);
    const { item_id } = ownLine;
    const excludedItemIds = without(
      selectedItemIds,
      undefined,
      null,
      '',
      item_id,
    );
    return excludedItemIds;
  },
);

export const getFilterBrands = createSelector(
  [getOwnBrand],
  brands => {
    return brands;
  },
);

export const getFilterClassifications = createSelector(
  [getOwnClassification],
  classifications => {
    return classifications;
  },
);

export const getFilterAlternateClass = createSelector(
  [getOwnAlternateClass],
  alternateClass => {
    return alternateClass;
  },
);

export const isViewMode = createSelector(
  [getFormStatus],
  status => {
    return (
      [PENDING_RECEIPT, PARTIALLY_RECEIVED, CLOSED, CLOSED_ABANDONED].indexOf(
        status.cid,
      ) > -1
    );
  },
);

export const isSerializedItem = createSelector(
  [getOwnLine],
  line => {
    return line.item_type_id === SERIALIZED;
  },
);

export const isPartQuantity = createSelector(
  [getOwnLine, getEnableItemPartQuantityPreference],
  (line, enableItemPartQuantity) => {
    const { item } = line;
    return enableItemPartQuantity && (item || {}).enable_part_quantity;
  },
);

export const viewModeForm = createSelector(
  [getFormStatus, getPropViewMode],
  (status, viewMode) => {
    return (
      [PENDING_RECEIPT, PARTIALLY_RECEIVED, CLOSED, CLOSED_ABANDONED].indexOf(
        status.cid,
      ) > -1 || viewMode
    );
  },
);

export const isClosed = createSelector(
  [getFormStatus],
  status => {
    return [CLOSED, CLOSED_ABANDONED].indexOf(status.cid) > -1;
  },
);

export const getValidCurrency = createSelector(
  [getOwnLocationCurrency, getOwnSupplierCurrency],
  (locationCurrency, supplierCurrency) => {
    const locationCurrencyId = (locationCurrency || {}).id;
    const supplierCurrencyId = (supplierCurrency || {}).id;
    const includedCurrencies = [];

    if (locationCurrencyId) includedCurrencies.push(locationCurrencyId);
    if (supplierCurrencyId) includedCurrencies.push(supplierCurrencyId);

    return uniq(includedCurrencies);
  },
);

export const getLocationCurrencyCode = createSelector(
  [getOwnLocationCurrency],
  locationCurrency => {
    return (locationCurrency || {}).code || 'USD';
  },
);

export const getLocationCurrencyId = createSelector(
  [getOwnLocationCurrency],
  locationCurrency => {
    return (locationCurrency || {}).id;
  },
);

export const allowGenerateReceipt = createSelector(
  [getFormStatus],
  status => {
    return [PENDING_RECEIPT, PARTIALLY_RECEIVED].indexOf(status.cid) > -1;
  },
);

export const getUnfilledLineItemIndex = createSelector(
  [getFormItemLines],
  lines => {
    return lines.findIndex(line => {
      return line && !line.quantity && !line.item_id;
    });
  },
);

export const hasAttachedPurchaseReceipts = createSelector(
  [getFormPurchaseReceipts],
  purchaseReceipts => {
    return Boolean((purchaseReceipts || []).length);
  },
);

export const getLineTax = createSelector(
  [getOwnLine],
  line => {
    const { total, total_ex_tax } = line;
    return (total || 0) - (total_ex_tax || 0);
  },
);

export const getLineItemUom = createSelector(
  [getOwnLine],
  line => {
    const { item_uom } = line;
    return item_uom;
  },
);


export const getLineReceivedQty = createSelector(
  [getOwnLine],
  line => {
    const { quantity, remaining_quantity, item_uom, uom_quantity, uom_remaining_quantity } = line;
    if (item_uom) {
      return Number(uom_quantity) - Number(uom_remaining_quantity);
    }
    return Number(quantity || 0) - Number(remaining_quantity || 0);
  },
);

export const getLineRemainingQty = createSelector(
  [getOwnLine],
  line => {
    const { item_uom, uom_remaining_quantity, remaining_quantity } = line;
    if (item_uom) {
      return uom_remaining_quantity || 0;
    }
    return remaining_quantity || 0;
  },
);

export const getLineIsFetchingItemLocation = createSelector(
  [getOwnLine],
  line => {
    const { isFetchingItemLocation } = line;
    return isFetchingItemLocation;
  },
);

export const getLineIsFetchingTaxGroup = createSelector(
  [getOwnLine],
  line => {
    const { isFetchingTaxGroup } = line;
    return isFetchingTaxGroup;
  },
);

export const getLineOnEditMode = createSelector(
  [getOwnLine],
  line => {
    const { onEditMode } = line;
    return onEditMode;
  },
);

export const getLineId = createSelector(
  [getOwnLine],
  line => {
    const { line_id } = line;
    return line_id;
  },
);

export const getFormLines = createSelector(
  [getFormItemLines],
  lines => {
    return lines;
  },
);

export const isExistingOrder = createSelector(
  [getFormId],
  id => {
    return Boolean(id);
  },
);

export const hasLines = createSelector(
  [getFormItemLines],
  lines => Boolean(lines.length),
);
